import React, { useContext, useEffect, useState } from 'react';
import { DraggableItemData } from '../../pages/Common/DraggableItem';
import { DraggableItem, ExportConfiguration } from '../../../types/export/exportConfiguration';
import Icon, { IconType } from '../../icons/Icon';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import { translate } from '../../../types/translation/Translator';
import { TextInput } from '../TextInput';
import { DeleteIconButton } from '../../icons';
import LoadingPane from '../Loading/LoadingPane';
import { DropdownSearchSelect, DropdownSelect } from '../Dropdown';
import { ExportType } from '../../../types/export/export';
import { Checkbox } from '../Checkbox';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { DraggableList } from '../../pages/Common/DraggableList';
import { CustomFieldContext } from '../../../context/CustomFieldContext';

interface ExportConfigurationMapping<F> {
  value: string;
  index: number;
  field: F;
}

export interface DraggableExportFieldIdentifier<F> extends DraggableItemData, ExportConfigurationMapping<F> {
  label: string;
}

interface ExportConfigurationFormProps<T extends ExportConfiguration<F>, F> {
  input: T;
  setInput: React.Dispatch<React.SetStateAction<T>>;
  footer: (configuration: T, items: DraggableExportFieldIdentifier<F>[]) => JSX.Element;
  loading?: boolean;

  draggableItems: DraggableExportFieldIdentifier<F>[];
  toDraggableItems: (input: T) => DraggableItem<F>[];

  placeholder: string;
  additionalOptions?: (input: T, setInput: React.Dispatch<React.SetStateAction<T>>) => JSX.Element;
}

export default function ExportConfigurationForm<T extends ExportConfiguration<F>, F>({
  input,
  setInput,
  footer,
  loading = false,

  draggableItems,
  toDraggableItems,

  placeholder,
  additionalOptions,
}: ExportConfigurationFormProps<T, F>) {
  const { customFields } = useContext(CustomFieldContext);

  const getItems = (input: T): DraggableExportFieldIdentifier<F>[] => {
    return toDraggableItems(input).map(item => {
      const draggableItem = draggableItems.find(i => i.field === item.field);
      return { ...item, label: draggableItem?.label ?? String(item.field) };
    });
  };

  const [items, setItems] = useState<DraggableExportFieldIdentifier<F>[]>(getItems(input));

  const ReportFieldItem = (item: DraggableExportFieldIdentifier<F>) => {
    const label = item.label;

    return (
      <div key={`parent-${item.id}`} className='grid w-full grid-cols-12 py-2'>
        <div className='col-span-3 flex items-center gap-12'>
          <Icon stroke='stroke-black' type={IconType.switchVertical1} />
          <p>{label}</p>
        </div>
        <div className='col-span-9'>
          <div className='flex items-center gap-2'>
            <div>
              <Icon stroke='stroke-black' type={IconType.arrowRight} />
            </div>
            <div className='flex-grow hover:cursor-text'>
              <TextInput
                value={item.value}
                onChange={v => {
                  item.value = v;
                  setItems([...items]);
                }}
                placeholder={label}
              />
            </div>
            <div>
              <DeleteIconButton onClick={() => handleDelete(item.id)} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleDelete = (id: number) => {
    const index = items.findIndex(item => item.id === id);
    if (index === -1) return;
    items.splice(index, 1);
    setItems([...items]);
  };

  const handleAdd = (item: DraggableExportFieldIdentifier<F>) => {
    setItems([...items, item]);
  };

  useEffect(() => {
    if (!items.length) setItems(getItems(input));
  }, [input]);

  const dropdownItems: DraggableExportFieldIdentifier<F>[] = [
    ...draggableItems.filter(i => items.findIndex(item => item.field === i.field) === -1),
  ];

  if (loading) return <LoadingPane />;

  return (
    <div className='content-x flex h-full flex-col justify-between'>
      <div className='flex flex-grow flex-col gap-2 overflow-hidden'>
        <div className='grid grid-cols-2 gap-2'>
          <div className='col-span-1'>
            <TextInput
              mandatory
              value={input.name}
              label={translate({ key: TranslationKey.name })}
              placeholder={translate({ key: TranslationKey.name })}
              onChange={v => setInput(config => config.withName(v))}
            />
          </div>
          <div className='col-span-1'>
            <TextInput
              mandatory
              value={input.filename}
              label={translate({ key: TranslationKey.fileName })}
              placeholder={translate({ key: TranslationKey.fileName })}
              onChange={v => setInput(config => config.withFilename(v))}
            />
          </div>
        </div>
        <div className='grid grid-cols-2 gap-2'>
          <div className='col-span-1'>
            <DropdownSelect<ExportType>
              value={input.type}
              placeholder={translate({ key: TranslationKey.type })}
              items={Object.values(ExportType)}
              textBuilder={item => item}
              onSelect={v => setInput(config => config.withType(v))}
            />
          </div>
        </div>
        <div>
          <Checkbox
            value={input.default}
            label={translate({ key: TranslationKey.defaultConfiguration })}
            onChange={checked => setInput(config => config.withDefault(checked))}
          />
        </div>
        {additionalOptions?.(input, setInput)}
        <div>
          <DropdownSearchSelect
            placeholder={placeholder}
            items={dropdownItems}
            textBuilder={item => item.label}
            value={undefined}
            onSelect={(item: DraggableExportFieldIdentifier<F> | undefined) => {
              if (item) handleAdd({ ...item, index: items.length, value: item.label });
            }}
          />
        </div>
        <div className='flex-grow overflow-auto'>
          <DndProvider backend={HTML5Backend}>
            <DraggableList displayFn={ReportFieldItem} items={items} setItems={setItems} />
          </DndProvider>
        </div>
      </div>
      <div>{footer(input, items)}</div>
    </div>
  );
}
